import { useEffect, useRef, useState } from 'react';

const STATS = [
  { value: 99.4, suffix: '%', label: 'Delivery verification rate' },
  { value: 12000, suffix: '+', label: 'Audit PDFs generated' },
  { value: 30, suffix: 's', label: 'To defend a delivery dispute' },
  { value: 4, suffix: '', label: 'Layers of chain-of-custody verification' },
] as const;

export function Stats() {
  return (
    <section className="bg-white py-20">
      <div className="container-page">
        <div className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-border bg-border lg:grid-cols-4">
          {STATS.map((stat) => (
            <Stat key={stat.label} {...stat} />
          ))}
        </div>
        <p className="mt-4 text-center text-xs text-ink-3">
          Projections based on chain-of-custody validation in CNC Delivery Service operations. Replaced with
          customer-derived numbers as LYN Rx onboards its first pharmacies.
        </p>
      </div>
    </section>
  );
}

function Stat({ value, suffix, label }: { value: number; suffix: string; label: string }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (!first || !first.isIntersecting || started.current) return;
        started.current = true;
        const start = performance.now();
        const duration = 1400;
        const tick = (now: number) => {
          const t = Math.min(1, (now - start) / duration);
          const eased = 1 - Math.pow(1 - t, 3);
          setDisplay(value * eased);
          if (t < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      },
      { threshold: 0.4 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [value]);

  const formatted =
    value >= 1000
      ? Math.round(display).toLocaleString('en-US')
      : Number.isInteger(value)
        ? Math.round(display).toString()
        : display.toFixed(1);

  return (
    <div ref={ref} className="bg-white p-8 text-center">
      <div className="text-4xl font-semibold tracking-tight text-ink md:text-5xl">
        {formatted}
        <span className="text-primary">{suffix}</span>
      </div>
      <div className="mt-2 text-sm text-ink-2">{label}</div>
    </div>
  );
}
